import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db, collections } from '../../firebase.marketplace';
import { collection, query, where, getDocs, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { processPayment } from '../../utils/paymentService';
import { PLATFORM_FEE_PERCENTAGE, getTransactionBreakdown, formatUGX } from '../../utils/feeCalculator';
import { 
  TextField, Button, MenuItem, Snackbar, Alert, CircularProgress, 
  Box, Typography, Paper, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Grid, Card, CardContent,
  Divider, Chip, IconButton, Tooltip, DialogTitle, Dialog, DialogContent,
  DialogActions, DialogContentText, ButtonGroup, ToggleButtonGroup, ToggleButton,
  createTheme, ThemeProvider, styled, useMediaQuery
} from '@mui/material';
import { 
  AttachMoney as MoneyIcon,
  AccountBalance as BankIcon,
  History as HistoryIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  ArrowUpward as ArrowUpIcon,
  MobileScreenShare as MobileIcon,
  Info as InfoIcon,
  Notifications as NotificationsIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Custom styled components
// Create a custom TextField with white text
const WhiteTextField = styled(TextField)({
  '& .MuiInputLabel-root': {
    color: 'white',
  },
  '& .MuiInputBase-input': {
    color: 'white',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4a90e2',
    },
  },
  '& .MuiFormHelperText-root': {
    color: 'white',
  },
  '& .MuiSvgIcon-root': {
    color: 'white',
  },
});

// Create a card with gradient background
// Card to show transaction breakdown
const FeeBreakdownCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  marginTop: theme.spacing(3),
  boxShadow: 'none',
  position: 'relative',
  overflow: 'visible',
  transition: 'all 0.3s ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    borderRadius: 'inherit',
    padding: '1px',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    WebkitMask: 
      'linear-gradient(#fff 0 0) content-box, ' +
      'linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    pointerEvents: 'none'
  }
}));

const GradientCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(45deg, #0a1128 30%, #1a2a6c 90%)',
  borderRadius: 3,
  boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
  overflow: 'hidden',
  height: '100%',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255,255,255,0.03)',
    zIndex: 0
  }
}));

const StatusChip = ({ status }) => {
  let color, icon, label;
  
  switch(status) {
    case 'completed':
    case 'paid':
      color = 'success';
      icon = <CheckIcon />;
      label = 'Paid';
      break;
    case 'pending':
      color = 'warning';
      icon = <PendingIcon />;
      label = 'Pending';
      break;
    case 'failed':
      color = 'error';
      icon = <ErrorIcon />;
      label = 'Failed';
      break;
    default:
      color = 'default';
      icon = <PendingIcon />;
      label = status || 'Unknown';
  }
  
  return (
    <Chip 
      icon={icon} 
      label={label} 
      color={color} 
      size="small" 
      variant="outlined"
    />
  );
};

// Create theme for the form
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#4a90e2',
    },
    success: {
      main: '#43a047',
    },
  },
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'white',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: 'white',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#4a90e2',
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: 'white',
        },
      },
    },
  },
});

// Constants
const MIN_WITHDRAWAL = 5000; // Minimum withdrawal amount
const SUGGESTED_AMOUNTS = [5000, 10000, 20000, 50000, 100000, 200000, 500000];

const Withdraw = () => {
  const { currentUser, userRoles } = useAuth();
  const isMobile = useMediaQuery('(max-width:600px)');
  const [earnings, setEarnings] = useState({ total: 0, available: 0, pending: 0 });
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawNumber, setWithdrawNumber] = useState('');
  const [withdrawProvider, setWithdrawProvider] = useState('mtn');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [withdrawError, setWithdrawError] = useState('');
  const [withdrawSuccess, setWithdrawSuccess] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [phoneValid, setPhoneValid] = useState(false);
  const [amountValid, setAmountValid] = useState(false);
  const [recentWithdrawal, setRecentWithdrawal] = useState(null);

  useEffect(() => {
    fetchEarnings();
    fetchWithdrawals();
    
    // Attempt to get saved phone number from previous withdrawal
    if (currentUser && withdrawals.length > 0) {
      const lastWithdrawal = withdrawals[0];
      if (lastWithdrawal.number) {
        setWithdrawNumber(lastWithdrawal.number);
        setWithdrawProvider(lastWithdrawal.provider || 'mtn');
      }
    }
    // eslint-disable-next-line
  }, [currentUser]);

  const fetchEarnings = async () => {
    if (!currentUser) return;
    try {
      const ordersSnap = await getDocs(query(collection(db, collections.ORDERS), where('artistId', '==', currentUser.uid), where('status', '==', 'completed')));
      let total = 0;
      ordersSnap.forEach(doc => {
        const order = doc.data();
        total += Number(order.payoutAmount || 0);
      });
      let pending = 0;
      let paid = 0;
      const withdrawalsSnap = await getDocs(query(collection(db, 'withdrawals'), where('artistId', '==', currentUser.uid)));
      withdrawalsSnap.forEach(doc => {
        const w = doc.data();
        if (w.status === 'pending') pending += Number(w.amount || 0);
        if (w.status === 'paid' || w.status === 'completed') paid += Number(w.amount || 0);
      });
      setEarnings({
        total,
        available: total - pending - paid,
        pending
      });
    } catch (err) {
      console.error('Error fetching earnings:', err);
      setEarnings({ total: 0, available: 0, pending: 0 });
      // Don't show error to user - just silently handle it
      // This follows the pattern from the authentication error handling
    }
  };

  const fetchWithdrawals = async () => {
    if (!currentUser) return;
    try {
      const withdrawalsSnap = await getDocs(query(collection(db, 'withdrawals'), where('artistId', '==', currentUser.uid)));
      const list = [];
      withdrawalsSnap.forEach(doc => {
        const data = doc.data();
        list.push({ 
          id: doc.id, 
          ...data,
          // Convert Firebase timestamps to JS dates
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().getTime() : data.createdAt
        });
      });
      const sortedList = list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setWithdrawals(sortedList);
      
      // Check for recent withdrawal (within the last hour)
      const now = Date.now();
      const oneHourAgo = now - (60 * 60 * 1000);
      const recent = sortedList.find(w => 
        w.createdAt && w.createdAt > oneHourAgo && w.status === 'pending'
      );
      setRecentWithdrawal(recent || null);
    } catch (err) {
      console.error('Error fetching withdrawals:', err);
      setWithdrawals([]);
    }
  };

  // Validate phone number format
  useEffect(() => {
    setPhoneValid(/^2567\d{8}$/.test(withdrawNumber));
  }, [withdrawNumber]);
  
  // Validate amount
  useEffect(() => {
    const amount = Number(withdrawAmount);
    const valid = !isNaN(amount) && amount >= MIN_WITHDRAWAL && amount <= earnings.available;
    setAmountValid(valid);
  }, [withdrawAmount, earnings.available]);
  
  // Handle amount suggestion selection
  const handleAmountSelect = (amount) => {
    if (amount <= earnings.available) {
      setWithdrawAmount(amount.toString());
    } else {
      setSnackbar({
        open: true,
        message: 'Selected amount exceeds your available balance',
        severity: 'warning'
      });
    }
  };
  
  // Open confirmation dialog
  const handleConfirmOpen = () => {
    // Validate before showing confirmation
    if (!withdrawAmount || isNaN(withdrawAmount) || Number(withdrawAmount) < MIN_WITHDRAWAL) {
      setWithdrawError(`Minimum withdrawal amount is ${MIN_WITHDRAWAL.toLocaleString('en-UG')} UGX.`);
      return;
    }
    if (Number(withdrawAmount) > earnings.available) {
      setWithdrawError('Amount exceeds available balance.');
      return;
    }
    if (!/^2567\d{8}$/.test(withdrawNumber)) {
      setWithdrawError('Enter a valid Ugandan phone number (format: 2567XXXXXXXX).');
      return;
    }
    
    setWithdrawError('');
    setConfirmOpen(true);
  };
  
  // Close confirmation dialog
  const handleConfirmClose = () => {
    setConfirmOpen(false);
  };
  
  // Calculate a fee breakdown when the withdrawal amount changes
  const [feeBreakdown, setFeeBreakdown] = useState({
    gross: 0,
    fee: 0,
    net: 0,
    feePercentage: PLATFORM_FEE_PERCENTAGE
  });

  // Update fee breakdown when withdrawal amount changes
  useEffect(() => {
    if (withdrawAmount && !isNaN(withdrawAmount) && Number(withdrawAmount) > 0) {
      // Note: For withdrawals, we don't actually apply the fee, this is just for display
      // to remind artists about the fee structure that was already applied to their earnings
      const withdrawalAmount = Number(withdrawAmount);
      setFeeBreakdown({
        gross: withdrawalAmount,
        fee: 0, // No additional fee on withdrawals
        net: withdrawalAmount,
        feePercentage: PLATFORM_FEE_PERCENTAGE
      });
    }
  }, [withdrawAmount]);
  
  // Process the withdrawal
  const handleWithdraw = async () => {
    setWithdrawError('');
    setWithdrawSuccess('');
    setWithdrawLoading(true);
    setConfirmOpen(false);
    
    try {
      // Get artist information for notification
      let artistInfo = { name: 'Unknown Artist' };
      try {
        const artistSnap = await getDocs(query(collection(db, 'artists'), where('userId', '==', currentUser.uid)));
        if (!artistSnap.empty) {
          artistInfo = artistSnap.docs[0].data();
        }
      } catch (err) {
        console.error('Error fetching artist info:', err);
        // Silently continue - we don't want to block the withdrawal
      }
      
      // Verify the minimum withdrawal amount
      const withdrawalAmount = Number(withdrawAmount);
      if (withdrawalAmount < MIN_WITHDRAWAL) {
        setWithdrawError(`Minimum withdrawal amount is ${MIN_WITHDRAWAL.toLocaleString('en-UG')} UGX.`);
        setWithdrawLoading(false);
        return;
      }

      // Format phone number for API
      let formattedNumber = withdrawNumber;
      // The API expects the phone number in the format accepted by the payment service
      // We're ensuring it's in the correct format as per paymentService.js

      // Generate unique reference with timestamp to avoid collisions
      const timestamp = Date.now();
      const reference = `withdrawal-${currentUser.uid}-${timestamp}`;

      // First create record in Firestore for tracking
      let withdrawalId;
      try {
        const docRef = await addDoc(collection(db, 'withdrawals'), {
          artistId: currentUser.uid,
          artistName: artistInfo.name || currentUser.displayName || currentUser.email,
          amount: withdrawalAmount,
          number: withdrawNumber,
          provider: withdrawProvider,
          reference: reference,
          status: 'pending',
          createdAt: serverTimestamp(),
          notes: null,
          payoutDate: null,
          requestTimestamp: timestamp
        });
        withdrawalId = docRef.id;
        console.log('Created withdrawal record with ID:', withdrawalId);
      } catch (err) {
        console.error('Error creating withdrawal record:', err);
        // Continue anyway - we still want to try processing the payment
      }
      
      // Now process the payment using the API
      console.log('Sending withdrawal request to payment API:', {
        amount: withdrawalAmount,
        number: formattedNumber,
        refer: reference,
        provider: withdrawProvider
      });

      const result = await processPayment({
        amount: withdrawalAmount,
        number: formattedNumber,
        refer: reference,
        provider: withdrawProvider
      });
      
      if (result.success || result.status === 'success') {
        // Success! Refresh data
        await fetchEarnings();
        await fetchWithdrawals();
        
        // Show success message
        setWithdrawSuccess('Withdrawal request submitted successfully!');
        setSnackbar({ 
          open: true, 
          message: 'Withdrawal request submitted successfully! You will receive a confirmation SMS shortly.', 
          severity: 'success' 
        });
        
        // Clear form
        setWithdrawAmount('');
        // Don't clear phone number - users typically withdraw to same number
        // setWithdrawNumber('');
        // setWithdrawProvider('mtn');
        
        // Attempt to notify admins
        try {
          await addDoc(collection(db, 'notifications'), {
            type: 'withdrawal',
            message: `New withdrawal request: ${Number(withdrawAmount).toLocaleString('en-UG')} UGX by ${artistInfo.name || currentUser.displayName || currentUser.email}`,
            createdAt: serverTimestamp(),
            read: false,
            for: ['admin', 'superadmin'],
            data: {
              amount: Number(withdrawAmount),
              artistId: currentUser.uid,
              reference: reference
            }
          });
        } catch (err) {
          console.error('Error sending admin notification:', err);
          // Silently continue - notification is optional
        }
      } else {
        setWithdrawError(result.message || 'Withdrawal failed.');
        setSnackbar({ open: true, message: result.message || 'Withdrawal failed.', severity: 'error' });
      }
    } catch (err) {
      console.error('Withdrawal processing error:', err);
      setWithdrawError(err.message || 'Withdrawal failed.');
      setSnackbar({ open: true, message: err.message || 'Withdrawal failed.', severity: 'error' });
    }
    
    setWithdrawLoading(false);
  };

  return (
    <ThemeProvider theme={darkTheme}>
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 6, px: 2, color: 'white' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
          <MoneyIcon sx={{ mr: 1, fontSize: 32, color: '#43a047' }} /> 
          Withdraw Funds
        </Typography>
        <Chip 
          label={`Available: ${earnings.available.toLocaleString('en-UG')} UGX`} 
          color="success" 
          icon={<BankIcon />}
          sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2.5, px: 1 }}
        />
      </Box>
      
      {/* Earnings Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          {/* Earnings Summary Cards */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
            <GradientCard sx={{ flex: 1, background: 'linear-gradient(45deg, #1a237e 30%, #283593 90%)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                  <BankIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Total Earnings
                </Typography>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                  {earnings.total.toLocaleString('en-UG')} UGX
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
                  After {PLATFORM_FEE_PERCENTAGE}% platform fees
                </Typography>
              </CardContent>
            </GradientCard>
            
            <GradientCard sx={{ flex: 1, background: 'linear-gradient(45deg, #006064 30%, #00838f 90%)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                  <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Available Balance
                </Typography>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                  {earnings.available.toLocaleString('en-UG')} UGX
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
                  Available for withdrawal
                </Typography>
              </CardContent>
            </GradientCard>
            
            <GradientCard sx={{ flex: 1, background: 'linear-gradient(45deg, #bf360c 30%, #e64a19 90%)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
                  <PendingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Pending Withdrawals
                </Typography>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                  {earnings.pending.toLocaleString('en-UG')} UGX
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 1 }}>
                  Withdrawals being processed
                </Typography>
              </CardContent>
            </GradientCard>
          </Box>
          
          {/* Platform Fee Explainer */}
          <FeeBreakdownCard>
            <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}>
              <InfoIcon sx={{ mr: 1, color: '#64b5f6' }} />
              Platform Fee Information
            </Typography>
            <Typography variant="body2" sx={{ color: 'white', mb: 2 }}>
              Konzo Vibez applies a {PLATFORM_FEE_PERCENTAGE}% platform fee on all marketplace transactions. Your earnings shown above are already calculated after this fee.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mt: 1 }}>
              <Box sx={{ flex: 1, p: 1.5, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.2)' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>
                  Example Transaction:
                </Typography>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  <strong>Sale price:</strong> 100,000 UGX
                </Typography>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  <strong>Platform fee ({PLATFORM_FEE_PERCENTAGE}%):</strong> 5,000 UGX
                </Typography>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  <strong>Your earnings:</strong> 95,000 UGX
                </Typography>
              </Box>
              <Box sx={{ flex: 1, p: 1.5, borderRadius: 1, bgcolor: 'rgba(0,100,0,0.2)' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 0.5 }}>
                  Benefits Include:
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', display: 'flex', alignItems: 'start', mb: 0.5 }}>
                  <CheckIcon sx={{ mr: 0.5, fontSize: '1rem', color: '#81c784' }} /> Worldwide visibility
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', display: 'flex', alignItems: 'start', mb: 0.5 }}>
                  <CheckIcon sx={{ mr: 0.5, fontSize: '1rem', color: '#81c784' }} /> Secure payment processing
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', display: 'flex', alignItems: 'start' }}>
                  <CheckIcon sx={{ mr: 0.5, fontSize: '1rem', color: '#81c784' }} /> Marketing & promotional support
                </Typography>
              </Box>
            </Box>
          </FeeBreakdownCard>
        </Grid>
      </Grid>
      
      {/* Withdrawal Form */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <GradientCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <ArrowUpIcon sx={{ fontSize: 24, color: '#43a047', mr: 1 }} />
                <Typography variant="h5" sx={{ color: 'white' }}>Request Withdrawal</Typography>
              </Box>
              
              <WhiteTextField
                label="Amount (UGX)"
                type="number"
                fullWidth
                margin="normal"
                value={withdrawAmount}
                onChange={e => setWithdrawAmount(e.target.value)}
                inputProps={{ min: 1, max: earnings.available, style: { color: 'white' } }}
                disabled={withdrawLoading}
                InputProps={{
                  startAdornment: <MoneyIcon sx={{ mr: 1, color: 'white' }} />,
                  style: { color: 'white' }
                }}
                helperText={`Minimum: ${MIN_WITHDRAWAL.toLocaleString('en-UG')} UGX | Maximum: ${earnings.available.toLocaleString('en-UG')} UGX`}
                FormHelperTextProps={{ style: { color: 'white' } }}
                sx={{ 
                  '& .MuiInputLabel-root': { color: 'white' },
                  '& .MuiOutlinedInput-root': { color: 'white' }
                }}
                error={withdrawAmount && !amountValid}
              />
              
              {/* Amount suggestions */}
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>Suggested amounts:</Typography>
                <ButtonGroup 
                  variant="outlined" 
                  size="small" 
                  sx={{ 
                    flexWrap: 'wrap', 
                    gap: 1,
                    '& .MuiButton-root': {
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.3)',
                      mb: 1
                    }
                  }}
                >
                  {SUGGESTED_AMOUNTS.map(amount => (
                    <Button 
                      key={amount} 
                      onClick={() => handleAmountSelect(amount)}
                      disabled={amount > earnings.available}
                    >
                      {amount.toLocaleString('en-UG')}
                    </Button>
                  ))}
                </ButtonGroup>
              </Box>
              
              <WhiteTextField
                label="Phone Number (MTN/Airtel, 2567XXXXXXXX)"
                fullWidth
                margin="normal"
                value={withdrawNumber}
                onChange={e => setWithdrawNumber(e.target.value)}
                disabled={withdrawLoading}
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ mr: 1, color: phoneValid ? 'green' : 'white' }} />,
                  style: { color: 'white' }
                }}
                inputProps={{ style: { color: 'white' } }}
                helperText="Enter your mobile money number (format: 2567XXXXXXXX)"
                FormHelperTextProps={{ style: { color: 'white' } }}
                sx={{ 
                  '& .MuiInputLabel-root': { color: 'white' },
                  '& .MuiOutlinedInput-root': { color: 'white' }
                }}
                error={withdrawNumber && !phoneValid}
              />
              
              <WhiteTextField
                label="Provider"
                select
                fullWidth
                margin="normal"
                value={withdrawProvider}
                onChange={e => setWithdrawProvider(e.target.value)}
                disabled={withdrawLoading}
                helperText="Select your mobile money provider"
                FormHelperTextProps={{ style: { color: 'white' } }}
                InputProps={{ style: { color: 'white' } }}
                inputProps={{ style: { color: 'white' } }}
                sx={{ 
                  '& .MuiInputLabel-root': { color: 'white' },
                  '& .MuiOutlinedInput-root': { color: 'white' },
                  '& .MuiSelect-select': { color: 'white' }
                }}
              >
                <MenuItem value="mtn">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <MobileIcon sx={{ mr: 1, color: '#ffcc00' }} />
                    <Typography sx={{ color: 'white' }}>MTN Mobile Money</Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="airtel">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <MobileIcon sx={{ mr: 1, color: '#ff0000' }} />
                    <Typography sx={{ color: 'white' }}>Airtel Money</Typography>
                  </Box>
                </MenuItem>
              </WhiteTextField>
              
              {withdrawError && <Alert severity="error" sx={{ mt: 2 }}>{withdrawError}</Alert>}
              {withdrawSuccess && <Alert severity="success" sx={{ mt: 2 }}>{withdrawSuccess}</Alert>}
              
              {recentWithdrawal && (
                <Alert 
                  severity="info" 
                  icon={<InfoIcon />}
                  sx={{ mt: 2, backgroundColor: 'rgba(33, 150, 243, 0.1)', color: 'white' }}
                >
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    You have a pending withdrawal of <strong>{Number(recentWithdrawal.amount).toLocaleString('en-UG')} UGX</strong> requested recently.
                  </Typography>
                </Alert>
              )}
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  onClick={handleConfirmOpen} 
                  variant="contained" 
                  color="success" 
                  size="large"
                  disabled={withdrawLoading || !amountValid || !phoneValid} 
                  startIcon={withdrawLoading ? <CircularProgress size={20} /> : <MoneyIcon />}
                  sx={{ 
                    py: 1.5, 
                    px: 4, 
                    borderRadius: 2,
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  }}
                >
                  {withdrawLoading ? 'Processing...' : 'Withdraw Funds'}
                </Button>
              </Box>
              
              {/* Confirmation Dialog */}
              <Dialog
                open={confirmOpen}
                onClose={handleConfirmClose}
                aria-labelledby="confirm-withdrawal-title"
                aria-describedby="confirm-withdrawal-description"
                PaperProps={{
                  sx: {
                    backgroundColor: '#0a1128',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    minWidth: isMobile ? '85vw' : '400px'
                  }
                }}
              >
                <DialogTitle id="confirm-withdrawal-title" sx={{ color: 'white' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <WarningIcon sx={{ mr: 1, color: '#ff9800' }} />
                    Confirm Withdrawal
                  </Box>
                </DialogTitle>
                <DialogContent>
                  <DialogContentText id="confirm-withdrawal-description" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    You are about to withdraw <strong>{Number(withdrawAmount).toLocaleString('en-UG')} UGX</strong> to <strong>{withdrawNumber}</strong> via <strong>{withdrawProvider.toUpperCase()}</strong>. Please confirm this is correct.
                  </DialogContentText>
                  <Typography variant="body2" sx={{ color: 'white', mt: 1, mb: 1 }}>
                    <InfoIcon sx={{ fontSize: '0.9rem', mr: 0.5, color: '#64b5f6', verticalAlign: 'middle' }} />
                    Remember: Your earnings already reflect the {PLATFORM_FEE_PERCENTAGE}% platform fee from sales.
                  </Typography>
                  <Box sx={{ mt: 2, p: 2, border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                      <strong>Amount:</strong> {Number(withdrawAmount).toLocaleString('en-UG')} UGX
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white', mb: 1 }}>
                      <strong>Phone:</strong> {withdrawNumber}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'white' }}>
                      <strong>Provider:</strong> {withdrawProvider.toUpperCase()}
                    </Typography>
                  </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                  <Button onClick={handleConfirmClose} color="inherit" sx={{ color: 'white' }}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleWithdraw} 
                    variant="contained" 
                    color="success"
                    startIcon={withdrawLoading ? <CircularProgress size={16} /> : null}
                    disabled={withdrawLoading}
                  >
                    Confirm Withdrawal
                  </Button>
                </DialogActions>
              </Dialog>
            </CardContent>
          </GradientCard>
        </Grid>
        
        {/* Withdrawal History */}
        <Grid item xs={12} md={6}>
          <GradientCard>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <HistoryIcon sx={{ fontSize: 24, color: '#4a90e2', mr: 1 }} />
                <Typography variant="h4" sx={{ mb: 2, color: 'white', textAlign: 'center' }}>Withdrawal History</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 3, color: 'rgba(255,255,255,0.7)', textAlign: 'center' }}>
                A {PLATFORM_FEE_PERCENTAGE}% platform fee has already been applied to your earnings. No additional fees are charged on withdrawals.
              </Typography>
              <TableContainer sx={{ maxHeight: 400, overflow: 'auto', background: 'rgba(0,0,0,0.2)', borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#4a90e2', fontWeight: 'bold', background: 'rgba(10,17,40,0.9)' }}>Date</TableCell>
                      <TableCell sx={{ color: '#4a90e2', fontWeight: 'bold', background: 'rgba(10,17,40,0.9)' }}>Amount</TableCell>
                      <TableCell sx={{ color: '#4a90e2', fontWeight: 'bold', background: 'rgba(10,17,40,0.9)' }}>Status</TableCell>
                      <TableCell sx={{ color: '#4a90e2', fontWeight: 'bold', background: 'rgba(10,17,40,0.9)' }}>Provider</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {withdrawals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: 'white', py: 4 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <HistoryIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.1)' }} />
                            <Typography>No withdrawals yet</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      withdrawals.map(w => (
                        <TableRow key={w.id} sx={{ '&:hover': { background: 'rgba(255,255,255,0.03)' } }}>
                          <TableCell sx={{ color: 'white' }}>
                            {w.createdAt ? new Date(w.createdAt).toLocaleString() : ''}
                          </TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                            {Number(w.amount).toLocaleString('en-UG')} UGX
                          </TableCell>
                          <TableCell>
                            <StatusChip status={w.status} />
                          </TableCell>
                          <TableCell sx={{ color: 'white', textTransform: 'capitalize' }}>
                            {w.provider}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {withdrawals.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    Showing {withdrawals.length} transaction{withdrawals.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </GradientCard>
        </Grid>
      </Grid>
      
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </ThemeProvider>
  );
};

export default Withdraw;
